/**
 * The start/stop clock on a task, from the browser's side.
 *
 * Four calls and the shapes they answer with. The rules they enforce live on the
 * server (`crm-projects/timers.routes.ts`) and are restated here only where a
 * screen has to react to one - which means the refusals, because every one of
 * them is a thing the operator can then do something about.
 *
 * A running clock is NOT a timesheet row. Stopping one is what writes the
 * `TimeEntry`, so a screen that lists hours must go on reading
 * `getTimeEntries` - nothing in this file will ever appear there on its own.
 */
import { ApiError, api } from "./api";
import type { TimeEntry } from "./projects";

/** One clock, running right now, with the task it is counting attached so the
 *  top bar can name it without a second request. */
export interface RunningTimer {
  id: string;
  taskId: string;
  /** → platform `User.id`. The clock's owner, and the only person allowed to
   *  stop it: stopping records hours in this name. */
  userId: string;
  startedAt: string;
  note: string | null;
  billable: boolean;
  createdAt: string;
  /** The SERVER's reading, at the moment the list was read. The screen ticks its
   *  own counter from `startedAt` instead; this is here so a stale clock can be
   *  drawn as stale without the browser having to agree about the time. */
  elapsedMinutes: number;
  /** Running for longer than a person plausibly worked. A stale clock cannot be
   *  stopped in one click - see `stopTaskTimer`. */
  stale: boolean;
  task: {
    id: string;
    title: string;
    projectId: string | null;
    project: { id: string; name: string } | null;
  } | null;
}

export interface RunningTimers {
  rows: RunningTimer[];
  /** The server's clock when it answered. The difference between this and the
   *  browser's own clock is drift, and correcting for it is why the counter on
   *  screen and the minutes that get recorded agree. */
  now: string;
  staleAfterMinutes: number;
}

/** What a stop asks for when the plain one has been refused, or when the
 *  operator opened the dialog to say something other than what the clock saw. */
export interface StopTimerBody {
  /** The minutes to record. Absent means "what the clock counted", which the
   *  server refuses past the stale line. */
  minutes?: number;
  note?: string;
  billable?: boolean;
}

export interface StopTimerResult {
  entry: TimeEntry;
  /** What the clock actually counted, whatever was recorded. The screen says
   *  both when they differ, so an adjustment is visible rather than silent. */
  measuredMinutes: number;
}

export const getRunningTimers = (signal?: AbortSignal) =>
  api.get<RunningTimers>("/admin/crm/task-timers", signal);

export const startTaskTimer = (taskId: string, body: { note?: string; billable?: boolean } = {}) =>
  api.post<RunningTimer>(`/admin/crm/tasks/${taskId}/timer`, body);

export const stopTaskTimer = (id: string, body: StopTimerBody = {}) =>
  api.post<StopTimerResult>(`/admin/crm/task-timers/${id}/stop`, body);

export const discardTaskTimer = (id: string) =>
  api.del<{ ok: boolean }>(`/admin/crm/task-timers/${id}`);

/**
 * The refusals a screen has to tell apart, and nothing else.
 *
 * `timer_running`: you already have a clock going. The failed response carries
 * it, which is what lets the screen offer to stop that one instead of leaving
 * the operator to find it.
 *
 * `timer_stale`: the clock has been going too long to be believed. The response
 * carries what it counted, so the dialog can open with a real figure in the
 * field rather than an empty box.
 *
 * `already_stopped` / `not_your_timer` / `not_found`: somebody or something else
 * got there first. All three mean "reload and look again", and the screen says
 * so in those words rather than showing a code.
 */
export type TimerErrorCode =
  | "timer_running"
  | "timer_stale"
  | "already_stopped"
  | "not_your_timer"
  | "not_found"
  | "unknown";

export interface TimerRefusal {
  code: TimerErrorCode;
  /** Present on `timer_running` only. */
  running?: RunningTimer;
  /** Present on `timer_stale` only: what the clock counted, in minutes. */
  elapsedMinutes?: number;
}

/**
 * Reads a failed call into something a screen can branch on.
 *
 * Anything that is not one of the known refusals comes back as `unknown`,
 * INCLUDING a network failure - a screen that cannot reach the server and a
 * screen that was told no are the same thing to the operator standing in front
 * of it: the clock did not do what they asked.
 */
export function timerRefusal(err: unknown): TimerRefusal {
  if (!(err instanceof ApiError) || !err.body || typeof err.body !== "object") return { code: "unknown" };
  const body = err.body as { error?: unknown; running?: RunningTimer; elapsedMinutes?: number };
  const known: TimerErrorCode[] = ["timer_running", "timer_stale", "already_stopped", "not_your_timer", "not_found"];
  const code = known.find((c) => c === body.error) ?? "unknown";
  return { code, running: body.running, elapsedMinutes: body.elapsedMinutes };
}
